/**
 * 상세 다이얼로그의 이전/다음이 따를 순서 — **화면이 지금 편 순서**다.
 *
 * 섬네일·카드·리스트는 카드 목록 순서 그대로 펴지만, 테이블은 제 정렬·페이지를 거쳐 다른 순서로 편다.
 * 그래서 테이블이 편 줄(글 원본)을 카드 key 로 되읽고, 그 key 순서로 카드를 다시 세운다.
 * ⚠️ 글 원본이 없는 카드(광고·상품)는 테이블에 서지 못한다 — 테이블 순서에서는 빠진다.
 */

import { toOrganicMediaCard } from './search-all-media-cards';

import type { MediaCard } from './search-all-media-cards';

/** 글 원본의 id — 테이블 줄과 카드를 잇는 열쇠다. 없으면 빈 문자열 */
export function postIdOf(post: Record<string, unknown> | null | undefined): string {
  const id = post?.id;
  return id == null ? '' : String(id);
}

/**
 * 테이블이 편 줄 → 카드 key(같은 순서).
 * @param cards 테이블에 넘긴 카드 목록
 * @param rows 테이블이 지금 편 줄(글 원본)
 * @returns 카드를 찾은 줄의 key 만, 줄 순서 그대로
 */
export function cardKeysOfRows(cards: MediaCard[], rows: readonly Record<string, unknown>[]): string[] {
  const byId = new Map<string, string>();
  cards.forEach((card) => {
    const id = postIdOf(card.post);
    if (id && !byId.has(id)) byId.set(id, card.key);
  });
  return rows.map((row) => byId.get(postIdOf(row)) ?? '').filter(Boolean);
}

/**
 * key 순서대로 카드를 다시 세운다.
 * @returns `keys` 에 있는 카드만 그 순서로 — 목록에 없는 key 는 버린다
 */
export function orderCardsByKeys(cards: MediaCard[], keys: readonly string[]): MediaCard[] {
  const byKey = new Map(cards.map((card) => [card.key, card]));
  return keys.map((key) => byKey.get(key)).filter((card): card is MediaCard => Boolean(card));
}

/** 두 줄이 같은가(칸마다 `===`) — 같으면 순서를 다시 쥐지 않는다(다시 그리기 고리를 끊는다) */
export function sameList<T>(a: readonly T[] | null, b: readonly T[] | null): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((item, i) => item === b[i]);
}

/**
 * 화면에 편 글 → 상세가 넘길 카드(같은 순서 · 같은 자리).
 * ⚠️ key 가 겹치면 뒤 것에 `#n` 을 붙인다 — 리포스트를 펼친 목록은 같은 글이 두 번 설 수 있는데,
 *   key 가 같으면 이전/다음이 앞 자리로 튄다.
 * @returns `posts[i]` 의 카드가 `[i]` 에 온다
 */
export function toListCards(posts: readonly Record<string, unknown>[]): MediaCard[] {
  const seen = new Map<string, number>();
  return posts.map((post, i) => {
    const card = toOrganicMediaCard(post, i);
    const n = seen.get(card.key) ?? 0;
    seen.set(card.key, n + 1);
    return n === 0 ? card : { ...card, key: `${card.key}#${n}` };
  });
}

/**
 * 목록에서 그 글의 자리 — 같은 객체를 먼저 찾고, 없으면 카드 key(글 id · 링크)로 찾는다.
 * ⚠️ 부르는 쪽이 목록을 다시 지어 넘기면(정렬·변환) 같은 글이라도 객체가 다르다 — 그래서 key 로 한 번 더 본다.
 * @returns 못 찾으면 -1
 */
export function indexOfPost(posts: readonly Record<string, unknown>[], post: Record<string, unknown>): number {
  const same = posts.indexOf(post);
  if (same >= 0) return same;
  const key = toOrganicMediaCard(post, -1).key;
  if (key === 'organic:organic--1') return -1;
  return posts.findIndex((p, i) => toOrganicMediaCard(p, i).key === key);
}
